import { useCallback, useEffect, useState } from 'react'
import { parteDiariaEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import type {
  ContextoParteDiariaDto,
  EquipeParteDto,
  MveParteDto,
  ObraParteDto,
  ServicoParteDto,
} from '@/types/parteDiariaDtos'
import { gravarCache, lerCache } from './parteDiariaCache'
import { servicosDoPacote } from './parteDiariaOffline'

/**
 * Hooks da parte diária — CACHE PRIMEIRO, rede depois (stale-while-revalidate):
 * mostra na hora o que está guardado (pré-carga offline ou última visita),
 * revalida no servidor e troca quando chega. Sem rede — ou com sinal fraco, em
 * que o fetch só desiste depois do timeout — a tela já está utilizável.
 */

// ---- MVEs do mês -------------------------------------------------------------

export interface MvesParteState {
  items: MveParteDto[]
  loading: boolean
  error: string | null
  source: 'live' | 'cache' | null
  /** Recusa do servidor (perfil sem acesso ao módulo). */
  recusa: string | null
}

const MVES_VAZIO: MvesParteState = { items: [], loading: false, error: null, source: null, recusa: null }

export function useMvesParteDiaria(dt: string): MvesParteState {
  const [state, setState] = useState<MvesParteState>({ ...MVES_VAZIO, loading: true })
  useEffect(() => {
    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null, recusa: null }))
    ;(async () => {
      const key = `mves:${dt.slice(0, 7)}`
      const previo = await lerCache<MveParteDto[]>(key)
      if (cancelled) return
      if (previo) setState({ items: previo.value, loading: true, error: null, source: 'cache', recusa: null })
      try {
        const res = await parteDiariaEndpoints.mves(dt)
        if (cancelled) return
        void gravarCache(key, res.items)
        setState({ items: res.items, loading: false, error: null, source: 'live', recusa: null })
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiError && (err.status === 403 || err.status === 422)) {
          setState({ ...MVES_VAZIO, source: 'live', recusa: err.message })
          return
        }
        const msg = err instanceof Error ? err.message : String(err)
        const cache = await lerCache<MveParteDto[]>(key)
        if (cancelled) return
        setState(cache ? { items: cache.value, loading: false, error: msg, source: 'cache', recusa: null } : { ...MVES_VAZIO, error: msg })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dt])
  return state
}

// ---- Contexto (= precheck) --------------------------------------------------

export interface ContextoState {
  status: 'idle' | 'loading' | 'live' | 'cache' | 'offline' | 'erro'
  contexto: ContextoParteDiariaDto | null
  message: string | null
  /** Recusa do servidor ao montar o contexto (MVE fora da lista, perfil…). */
  recusa: { code: string; message: string } | null
}

const CTX_IDLE: ContextoState = { status: 'idle', contexto: null, message: null, recusa: null }

export async function buscarContexto(mveId: number, dt: string): Promise<ContextoState> {
  const key = `contexto:${mveId}:${dt}`
  try {
    const ctx = await parteDiariaEndpoints.contexto(mveId, dt)
    void gravarCache(key, ctx)
    return { status: 'live', contexto: ctx, message: null, recusa: null }
  } catch (err) {
    if (err instanceof ApiError && !err.isOffline && err.status >= 400 && err.status < 500) {
      return { status: 'erro', contexto: null, message: err.message, recusa: { code: err.code ?? `HTTP_${err.status}`, message: err.message } }
    }
    const cache = await lerCache<ContextoParteDiariaDto>(key)
    if (cache) {
      return {
        status: 'cache',
        contexto: cache.value,
        message: 'Sem rede — contexto do SIGA em cache. Bloqueios e horímetro serão conferidos no envio.',
        recusa: null,
      }
    }
    const offline = err instanceof ApiError && err.isOffline
    return {
      status: offline ? 'offline' : 'erro',
      contexto: null,
      message: offline
        ? 'Sem rede e sem cache deste equipamento hoje. Você pode lançar; as regras do SIGA serão conferidas no envio.'
        : `Não foi possível carregar o contexto agora (${err instanceof Error ? err.message : String(err)}).`,
      recusa: null,
    }
  }
}

export function useContextoParteDiaria(mveId: number | null | undefined, dt: string): ContextoState & { recarregar: () => void } {
  const [state, setState] = useState<ContextoState>(CTX_IDLE)
  const [nonce, setNonce] = useState(0)
  const recarregar = useCallback(() => setNonce((n) => n + 1), [])
  useEffect(() => {
    if (mveId == null) {
      setState(CTX_IDLE)
      return
    }
    let cancelled = false
    setState({ ...CTX_IDLE, status: 'loading' })
    buscarContexto(mveId, dt).then((r) => {
      if (!cancelled) setState(r)
    })
    return () => {
      cancelled = true
    }
  }, [mveId, dt, nonce])
  return { ...state, recarregar }
}

// ---- Listas por obra ----------------------------------------------------------

interface ListaState<T> {
  items: T[]
  loading: boolean
  source: 'live' | 'cache' | null
}

function useListaComCache<T>(
  key: string | null,
  fetcher: (() => Promise<T[]>) | null,
  /** Última linha de defesa sem rede e sem cache da chave (ex.: montar do pacote offline). */
  reserva?: () => Promise<T[] | null>,
): ListaState<T> {
  const [state, setState] = useState<ListaState<T>>({ items: [], loading: false, source: null })
  useEffect(() => {
    if (!key || !fetcher) {
      setState({ items: [], loading: false, source: null })
      return
    }
    let cancelled = false
    setState({ items: [], loading: true, source: null })
    ;(async () => {
      const local = (await lerCache<T[]>(key))?.value ?? (reserva ? await reserva() : null)
      if (cancelled) return
      if (local) setState({ items: local, loading: true, source: 'cache' })
      try {
        const items = await fetcher()
        if (cancelled) return
        void gravarCache(key, items)
        setState({ items, loading: false, source: 'live' })
      } catch {
        if (cancelled) return
        setState({ items: local ?? [], loading: false, source: local ? 'cache' : null })
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return state
}

/** `mveId` só muda o resultado para administrador: obras do chefe responsável pelo equipamento. */
export function useObrasParteDiaria(ativo: boolean, mveId?: number | null): ListaState<ObraParteDto> {
  return useListaComCache<ObraParteDto>(
    ativo ? (mveId != null ? `obras:m${mveId}` : 'obras') : null,
    ativo ? () => parteDiariaEndpoints.obras(mveId ?? undefined) : null,
    async () => (await lerCache<ObraParteDto[]>('obras'))?.value ?? null,
  )
}

export function useServicosParteDiaria(mveId: number | null, obraId: number | null): ListaState<ServicoParteDto> {
  const ok = mveId != null && obraId != null
  return useListaComCache<ServicoParteDto>(
    ok ? `servicos:${mveId}:${obraId}` : null,
    ok ? () => parteDiariaEndpoints.servicos(mveId, obraId) : null,
    ok ? () => servicosDoPacote(mveId, obraId) : undefined,
  )
}

export function useEquipesParteDiaria(obraId: number | null): ListaState<EquipeParteDto> {
  return useListaComCache<EquipeParteDto>(
    obraId != null ? `equipes:${obraId}` : null,
    obraId != null ? () => parteDiariaEndpoints.equipes(obraId) : null,
  )
}
